import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import axios from "axios";
import {
  Upload,
  X,
  CheckCircle,
  Trash2,
  Search,
  ChevronLeft,
  ChevronRight,
  Edit,
  ChevronUp,
  ChevronDown,
} from "lucide-react";
import Modal from "../../Modal/Modal";
import "./ClanTasksTab.css";

const API_BASE_URL = `${process.env.REACT_APP_API_BASE_URL}/api`.replace(
  /\/+$/,
  ""
);
const PAGE_SIZE_OPTIONS = [10, 25, 50];
const DIFFICULTY_OPTIONS = ["EASY", "MEDIUM", "HARD", "ELITE", "MASTER"];

function ClanTasksTab({
  clanId,
  clanName,
  isActionInProgress,
  isCurrentUserManager,
  setActionMessage,
  setIsProcessingAction,
}) {
  const [selectedFile, setSelectedFile] = useState(null);
  // Renamed tasks to allTasks to reflect its true content (all tasks, potentially from other clans)
  const [tasks, setTasks] = useState([]);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const fileInputRef = useRef(null);

  const [isDeleteAllModalOpen, setIsDeleteAllModalOpen] = useState(false);
  const [taskToEdit, setTaskToEdit] = useState(null);
  const [editFormData, setEditFormData] = useState({
    description: "",
    difficulty: "",
  });
  const [taskToDelete, setTaskToDelete] = useState(null);
  const [formError, setFormError] = useState(null);

  // NEW: State for task creation modal
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [createFormData, setCreateFormData] = useState({
    description: "",
    difficulty: "",
  });

  const [searchTerm, setSearchTerm] = useState("");
  // IMPORTANT: This state now controls which endpoint we hit in fetchTasks
  const [showOtherClanTasks, setShowOtherClanTasks] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);

  // NEW: State for importer minimization
  const [isImporterMinimized, setIsImporterMinimized] = useState(true);

  // UPDATED: State for sorting configuration - Default key changed to 'difficulty'
  const [sortConfig, setSortConfig] = useState({
    key: "difficulty",
    direction: "asc",
  });

  // UPDATED: fetchTasks to use the correct endpoint based on showOtherClanTasks
  const fetchTasks = useCallback(
    async (shouldFetchAll) => {
      setLoadingTasks(true);
      try {
        // Determine the correct endpoint
        const endpoint = shouldFetchAll
          ? `${API_BASE_URL}/tasks/` // NEW global endpoint
          : `${API_BASE_URL}/clan/${clanId}/tasks/`; // Existing clan-specific endpoint

        const response = await axios.get(endpoint, {
          withCredentials: true,
        });
        // The backend now returns all tasks when showOtherClanTasks is true,
        // otherwise it returns tasks only for the current clan.
        setTasks(response.data.tasks || []);
      } catch (err) {
        console.error("Failed to fetch clan tasks:", err);
        setActionMessage({
          type: "error",
          message: "Failed to load current tasks. Check console for details.",
        });
        setTasks([]);
      } finally {
        setLoadingTasks(false);
      }
    },
    [clanId, setActionMessage]
  );

  // UPDATED: useEffect now triggers fetchTasks with the current state of showOtherClanTasks
  useEffect(() => {
    // Pass the state to useCallback to ensure correct API call
    fetchTasks(isCurrentUserManager && showOtherClanTasks);
  }, [fetchTasks, showOtherClanTasks, isCurrentUserManager]); // Added dependency on showOtherClanTasks and isCurrentUserManager

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, pageSize, showOtherClanTasks, sortConfig]);

  // NEW: Toggle handler
  const handleToggleMinimize = () => {
    setIsImporterMinimized((prev) => !prev);
  };

  // NEW: Sort handler
  const handleSort = (key) => {
    let direction = "asc";
    // If the same key is clicked, toggle direction
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  // NEW: Helper for displaying sort indicator
  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return null;
    }
    return sortConfig.direction === "asc" ? " ▲" : " ▼";
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file && file.name.endsWith(".csv")) {
      setSelectedFile(file);
    } else {
      setSelectedFile(null);
      setActionMessage({
        type: "error",
        message: "Please select a valid .csv file.",
      });
    }
  };

  const clearFileSelection = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleUploadClick = async () => {
    if (!selectedFile) {
      setActionMessage({
        type: "error",
        message: "No file selected for upload.",
      });
      return;
    }

    setIsProcessingAction(true);
    setActionMessage(null);

    const formData = new FormData();
    formData.append("file", selectedFile);

    try {
      const endpoint = `${API_BASE_URL}/clan/${clanId}/tasks/upload_csv`;

      const response = await axios.post(endpoint, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        withCredentials: true,
      });

      clearFileSelection();
      // Refetch tasks based on the current toggle state
      fetchTasks(showOtherClanTasks);

      setActionMessage({
        type: "success",
        message: `Tasks extracted successfully! ${response.data.inserted_count} tasks imported/updated.`,
      });
    } catch (err) {
      console.error("CSV upload failed:", err);
      const errorMessage =
        err.response?.data?.error ||
        "Failed to extract tasks. Check file format and permissions.";
      setActionMessage({ type: "error", message: errorMessage });
    } finally {
      setIsProcessingAction(false);
    }
  };

  const openDeleteAllModal = () => {
    setIsDeleteAllModalOpen(true);
  };

  const closeDeleteAllModal = () => {
    setIsDeleteAllModalOpen(false);
  };

  const handleDeleteAllTasks = async () => {
    setIsProcessingAction(true);
    setActionMessage(null);

    try {
      const endpoint = `${API_BASE_URL}/clan/${clanId}/tasks/`;
      await axios.delete(endpoint, { withCredentials: true });

      // Refetch tasks based on the current toggle state
      fetchTasks(showOtherClanTasks);
      closeDeleteAllModal();

      setActionMessage({
        type: "success",
        message: "All clan tasks have been successfully deleted.",
      });
    } catch (err) {
      console.error("Delete all tasks failed:", err);
      const errorMessage =
        err.response?.data?.error ||
        "Failed to delete tasks. Check permissions.";
      setActionMessage({ type: "error", message: errorMessage });
      closeDeleteAllModal();
    } finally {
      setIsProcessingAction(false);
    }
  };

  // NEW: Create Task Handlers
  const openCreateModal = () => {
    setCreateFormData({ description: "", difficulty: "" }); // Reset form
    setFormError(null);
    setIsCreateModalOpen(true);
  };

  const closeCreateModal = () => {
    setIsCreateModalOpen(false);
    setFormError(null);
    setCreateFormData({ description: "", difficulty: "" });
  };

  const handleCreateFormChange = (e) => {
    const { name, value } = e.target;
    setCreateFormData((prev) => ({ ...prev, [name]: value }));
    setFormError(null);
  };

  const handleCreateTask = async () => {
    if (isActionInProgress) return;
    setIsProcessingAction(true);
    setActionMessage(null);
    setFormError(null);

    if (!createFormData.description.trim() || !createFormData.difficulty) {
      setFormError("Description and Difficulty are required.");
      setIsProcessingAction(false);
      return;
    }

    try {
      const endpoint = `${API_BASE_URL}/clan/${clanId}/tasks`;

      const response = await axios.post(endpoint, createFormData, {
        withCredentials: true,
      });

      // Refetch tasks based on the current toggle state to update the list
      fetchTasks(showOtherClanTasks);
      closeCreateModal();

      setActionMessage({
        type: "success",
        message: `Task '${response.data.task.description.substring(
          0,
          30
        )}...' created successfully.`,
      });
    } catch (err) {
      console.error("Task creation failed:", err);
      const errorMessage =
        err.response?.data?.error || "Failed to create task. Check form data.";
      setFormError(errorMessage);
      setActionMessage({ type: "error", message: errorMessage });
    } finally {
      setIsProcessingAction(false);
    }
  };
  // END NEW HANDLERS

  const handleEditClick = (task) => {
    setTaskToEdit(task);
    setEditFormData({
      description: task.description,
      difficulty: task.difficulty,
    });
    setFormError(null);
  };

  const closeEditModal = () => {
    setTaskToEdit(null);
    setFormError(null);
    setEditFormData({ description: "", difficulty: "" });
  };

  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditFormData((prev) => ({ ...prev, [name]: value }));
    setFormError(null);
  };

  const handleSaveTask = async () => {
    if (isActionInProgress) return;
    setIsProcessingAction(true);
    setActionMessage(null);
    setFormError(null);

    if (!editFormData.description.trim() || !editFormData.difficulty) {
      setFormError("Description and Difficulty are required.");
      setIsProcessingAction(false);
      return;
    }

    try {
      const endpoint = `${API_BASE_URL}/clan/${clanId}/tasks/${taskToEdit.id}`;

      const response = await axios.put(endpoint, editFormData, {
        withCredentials: true,
      });

      // Refetch tasks based on the current toggle state
      fetchTasks(showOtherClanTasks);
      closeEditModal();

      setActionMessage({
        type: "success",
        message: `Task '${response.data.task.description.substring(
          0,
          30
        )}...' updated successfully.`,
      });
    } catch (err) {
      console.error("Task update failed:", err);
      const errorMessage =
        err.response?.data?.error || "Failed to update task.";
      setFormError(errorMessage);
      setActionMessage({ type: "error", message: errorMessage });
    } finally {
      setIsProcessingAction(false);
    }
  };

  const handleDeleteClick = (task) => {
    setTaskToDelete(task);
  };

  const closeIndividualDeleteModal = () => {
    setTaskToDelete(null);
  };

  const handleConfirmIndividualDelete = async () => {
    if (isActionInProgress || !taskToDelete) return;
    setIsProcessingAction(true);
    setActionMessage(null);

    try {
      const endpoint = `${API_BASE_URL}/clan/${clanId}/tasks/${taskToDelete.id}`;
      await axios.delete(endpoint, { withCredentials: true });

      // Refetch tasks based on the current toggle state
      fetchTasks(showOtherClanTasks);
      closeIndividualDeleteModal();

      setActionMessage({
        type: "success",
        message: `Task '${taskToDelete.description.substring(
          0,
          30
        )}...' deleted successfully.`,
      });
    } catch (err) {
      console.error("Individual task deletion failed:", err);
      const errorMessage =
        err.response?.data?.error || "Failed to delete task.";
      setActionMessage({ type: "error", message: errorMessage });
      closeIndividualDeleteModal();
    } finally {
      setIsProcessingAction(false);
    }
  };

  const deleteTaskModalContent = (
    <>
      <p>
        Are you <strong>absolutely sure</strong> you want to DELETE ALL{" "}
        <strong>{tasks.length}</strong> tasks for the clan{" "}
        <strong style={{ color: "#e74c3c" }}>{clanName}</strong>?
      </p>
      <p>
        This action cannot be undone and will <strong>permanently</strong>{" "}
        remove all task definitions.
      </p>
    </>
  );

  const deleteIndividualTaskModalContent = taskToDelete ? (
    <>
      <p>Are you sure you want to delete the task:</p>
      <p className="task-delete-description">
        <strong>{taskToDelete.description}</strong> (Difficulty:{" "}
        {taskToDelete.difficulty})
      </p>
      <p>This action will permanently remove this task.</p>
    </>
  ) : null;

  // UPDATED: Renamed to renderTaskForm to be reusable for both Create and Edit
  const renderTaskForm = (formData, handleChange, error, isSaving) => (
    <div className="task-details-form-container">
      {error && <div className={`form-message error`}>{error}</div>}

      <form className="task-details-form" onSubmit={(e) => e.preventDefault()}>
        <div className="form-group">
          <label className="modal-form-label" htmlFor="description">
            Task Description
          </label>
          <textarea
            id="description"
            className="modal-form-field"
            name="description"
            value={formData.description}
            onChange={handleChange}
            disabled={isSaving}
            required
            rows="3"
          />
        </div>
        <div className="form-group">
          <label className="modal-form-label" htmlFor="difficulty">
            Difficulty
          </label>
          <select
            id="difficulty"
            className="modal-form-field"
            name="difficulty"
            value={formData.difficulty}
            onChange={handleChange}
            disabled={isSaving}
            required
          >
            <option value="">Select Difficulty</option>
            {DIFFICULTY_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>
      </form>
    </div>
  );

  // UPDATED: Filtering Logic is simplified. The fetchTasks now handles
  // which set of tasks is loaded (clan-only or all). The `tasks` state
  // contains only the data requested, so the previous check is no longer needed.
  // The only time we filter by clan is for the read-only view.
  const filteredTasks = useMemo(() => {
    let filtered = tasks;
    const lowerCaseSearch = searchTerm.toLowerCase();

    // 1. Filtering Logic (SEARCH ONLY)
    if (lowerCaseSearch) {
      filtered = filtered.filter(
        (task) =>
          (task.description &&
            task.description.toLowerCase().includes(lowerCaseSearch)) ||
          (task.difficulty &&
            task.difficulty.toLowerCase().includes(lowerCaseSearch)) ||
          (task.clan_name && // Allow searching by clan name when showing all tasks
            task.clan_name.toLowerCase().includes(lowerCaseSearch))
      );
    }

    // 2. Sorting Logic
    if (sortConfig.key) {
      // Create a copy to sort safely
      filtered = [...filtered].sort((a, b) => {
        const aValue = a[sortConfig.key] || "";
        const bValue = b[sortConfig.key] || "";

        // Handle null/undefined values by treating them as empty strings for comparison
        const aValueString = String(aValue);
        const bValueString = String(bValue);

        // LocaleCompare is better for string sorting
        const comparison = aValueString.localeCompare(bValueString);

        if (comparison < 0) {
          return sortConfig.direction === "asc" ? -1 : 1;
        }
        if (comparison > 0) {
          return sortConfig.direction === "asc" ? 1 : -1;
        }
        return 0;
      });
    }

    return filtered;
  }, [tasks, searchTerm, sortConfig]);

  const totalPages = Math.ceil(filteredTasks.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const currentTasksPage = filteredTasks.slice(startIndex, endIndex);

  const goToPrevPage = () => {
    setCurrentPage((prev) => Math.max(1, prev - 1));
  };

  const goToNextPage = () => {
    setCurrentPage((prev) => Math.min(totalPages, prev + 1));
  };

  const handlePageSizeChange = (e) => {
    setPageSize(Number(e.target.value));
  };

  const resultsCountMessage = useMemo(() => {
    const totalCount = tasks.length;
    const filteredCount = filteredTasks.length;

    if (filteredCount === 0) return "No tasks found";

    const start = (currentPage - 1) * pageSize + 1;
    const end = Math.min(start + pageSize - 1, filteredCount);

    let message = `${start}-${end} of ${filteredCount} tasks`;

    // Only show "Filtered from" if we're actively searching, not just filtering by clan toggle
    if (searchTerm && filteredCount < totalCount) {
      message += ` (Filtered from ${totalCount})`;
    } else if (showOtherClanTasks && !searchTerm) {
      message += ` (Showing all clans)`;
    }
    return message;
  }, [
    tasks.length,
    filteredTasks.length,
    currentPage,
    pageSize,
    searchTerm,
    showOtherClanTasks,
  ]);

  const isPrevDisabled = currentPage === 1;
  const isNextDisabled = currentPage === totalPages || totalPages === 0;
  // Used for both Create and Edit modals
  const isSaveDisabled =
    isActionInProgress ||
    !createFormData.description.trim() ||
    !createFormData.difficulty ||
    !editFormData.description.trim() ||
    !editFormData.difficulty;

  const renderTaskContent = (listToDisplay) => {
    if (loadingTasks) {
      return <p>Loading tasks...</p>;
    }
    if (listToDisplay.length === 0) {
      return <p>No tasks currently active.</p>;
    }

    return (
      <table className="task-table member-table">
        <thead>
          <tr>
            <th
              onClick={() => handleSort("description")}
              className="sortable-header"
              style={{ cursor: "pointer" }}
            >
              Description
              {getSortIcon("description")}
            </th>
            <th
              onClick={() => handleSort("difficulty")}
              className="sortable-header"
              style={{ cursor: "pointer" }}
            >
              Difficulty
              {getSortIcon("difficulty")}
            </th>
            {/* Show Clan Name column unconditionally for Manager view, 
              as we may now be fetching tasks from other clans. */}
            <th
              onClick={() => handleSort("clan_name")}
              className="sortable-header"
              style={{ cursor: "pointer" }}
            >
              Clan Name
              {getSortIcon("clan_name")}
            </th>
            <th className="action-col">Action</th>
          </tr>
        </thead>
        <tbody>
          {listToDisplay.map((task) => (
            <tr key={task.id}>
              <td data-label="Description">{task.description}</td>
              <td data-label="Difficulty">{task.difficulty}</td>
              <td data-label="Clan Name">{task.clan_name}</td>
              <td data-label="Actions" className="task-actions-cell action-col">
                {task.clan_name === clanName && (
                  <div className="action-buttons-group">
                    {" "}
                    {/* Added group wrapper for consistency */}
                    <button
                      className="btn-icon-small"
                      onClick={() => handleEditClick(task)}
                      disabled={isActionInProgress}
                      title="Edit Task"
                    >
                      <Edit size={16} />
                    </button>
                    <button
                      className="btn-icon-small btn-danger"
                      onClick={() => handleDeleteClick(task)}
                      disabled={isActionInProgress}
                      title="Delete Task"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  const renderReadOnlyTaskContent = (listToDisplay) => {
    if (loadingTasks) {
      return <p>Loading tasks...</p>;
    }
    // In the read-only view, we ensure the list only contains this clan's tasks
    const clanOnlyTasks = listToDisplay.filter(
      (task) => task.clan_name === clanName
    );

    if (clanOnlyTasks.length === 0) {
      return <p>No tasks currently active for this clan.</p>;
    }
    return (
      <ul className="list-no-style">
        {clanOnlyTasks.map((task) => (
          <li key={task.id} className="task-item-read-only">
            <strong>{task.description}</strong> ({task.difficulty})
          </li>
        ))}
      </ul>
    );
  };

  if (!isCurrentUserManager) {
    return (
      <div className="tab-content-panel">
        <p className="warning-message">
          <X size={20} className="icon-margin-right" />
          Only Clan Admins and the Owner can manage and upload tasks.
        </p>
        <div className="task-list">
          {/* Note: In the read-only view, the component's fetchTasks will only hit the 
            clan-specific endpoint, so tasks state should already be filtered. 
            The filter is only kept here as a safeguard. */}
          <h3>Current Clan Tasks ({tasks.length})</h3>
          {renderReadOnlyTaskContent(tasks)}
        </div>
      </div>
    );
  }

  return (
    <div className="tab-content-panel">
      {/* CSV UPLOAD SECTION with Minimization */}
      <div className="csv-upload-section">
        {/* Clickable header to toggle minimization */}
        <div
          className="importer-header"
          onClick={handleToggleMinimize}
          role="button"
          tabIndex="0"
          aria-expanded={!isImporterMinimized}
          aria-controls="csv-importer-content"
        >
          <h3>Task CSV Importer</h3>
          <div className="importer-toggle-icon">
            {isImporterMinimized ? (
              <ChevronDown size={25} />
            ) : (
              <ChevronUp size={25} />
            )}
          </div>
        </div>

        {/* Content area that collapses */}
        <div
          id="csv-importer-content"
          className={`importer-content-area ${
            isImporterMinimized ? "minimized" : ""
          }`}
        >
          <p>
            Use a CSV file to bulk import or update tasks for {clanName}.
            <br />
            The CSV must contain columns:{" "}
            <strong>description, difficulty</strong>.
            <br />
            Any duplicate rows with the same values will automatically be
            discarded.
            <br />
            <strong>Sample Format:</strong> You can view/download a sample
            template&nbsp;
            <a
              href="https://docs.google.com/spreadsheets/d/1scX_wQqoHVgYRMx1sfCLnwFE4dWOP5I8aVfpn7p2KKk/edit?usp=sharing"
              target="_blank"
              rel="noopener noreferrer"
            >
              here
            </a>
            .
          </p>

          {/* Clearly separated Notes section with task logic */}
          <div className="importer-note">
            <strong>Note:</strong> All tasks are displayed uniquely on the
            board, with the exception of Slayer and Raid tasks which allow for
            duplicates:
            <ul className="importer-note-list">
              <li>
                <strong>General Tasks:</strong> Appear only once per board.
              </li>
              <li>
                <strong>Raids Tasks:</strong> If the description contains{" "}
                <em>"Obtain any Raids unique"</em>, the system allows duplicates
                based on difficulty:
                <strong> Hard</strong> (max 1), <strong>Elite</strong> (max 2),{" "}
                <strong>Master</strong> (max 3).
              </li>
              <li>
                <strong>Slayer Tasks:</strong> If the description contains{" "}
                <em>"slayer boss unique"</em>, these act as filler tasks to fill
                remaining board slots.
              </li>
            </ul>
          </div>

          <div className="task-upload-controls">
            <input
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              ref={fileInputRef}
              disabled={isActionInProgress}
              className="file-input-display"
            />

            {selectedFile && (
              <div className="file-selection-info">
                <CheckCircle
                  size={16}
                  color="green"
                  className="icon-margin-right"
                />
                <span className="selected-filename">
                  Selected: <strong>{selectedFile.name}</strong>
                </span>
                <button
                  className="btn-link btn-danger"
                  onClick={clearFileSelection}
                  disabled={isActionInProgress}
                >
                  Clear
                </button>
              </div>
            )}

            <button
              className="btn-primary"
              onClick={handleUploadClick}
              disabled={!selectedFile || isActionInProgress}
            >
              <Upload size={18} className="icon-margin-right" />
              {isActionInProgress
                ? "Extracting Data..."
                : "Upload & Extract Tasks"}
            </button>
          </div>
        </div>
      </div>

      <hr className="divider-margin" />

      <h3>Current Available Tasks ({tasks.length})</h3>

      {/* NEW: Button to open the create task modal */}
      <div className="tasks-action-header">
        <button
          className="btn-success"
          onClick={openCreateModal}
          disabled={isActionInProgress}
        >
          <Edit size={18} className="icon-margin-right" />
          Create New Task
        </button>
      </div>
      {/* END NEW ACTION HEADER */}

      <div className="tasks-controls-top members-tab-controls-top">
        <div className="search-bar-container">
          <Search size={20} className="search-icon" />
          <input
            type="text"
            placeholder="Search by description, difficulty, or clan..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            disabled={isActionInProgress}
            className="search-input"
          />
        </div>

        <div className="tasks-filter-group">
          <div className="toggle-switch-container">
            <input
              type="checkbox"
              id="otherClansToggle"
              checked={showOtherClanTasks}
              // Toggle handler: simply flips the state. The useEffect hook
              // then detects the change and calls fetchTasks(true/false).
              onChange={() => setShowOtherClanTasks(!showOtherClanTasks)}
              disabled={isActionInProgress}
            />
            <label htmlFor="otherClansToggle" className="toggle-label">
              Show Other Clan Tasks
            </label>
            <label htmlFor="otherClansToggle">Show Other Clan Tasks</label>
          </div>
        </div>
      </div>

      {loadingTasks ? (
        <div className="spinner-small list-spinner-margin"></div>
      ) : filteredTasks.length === 0 ? (
        <p>No tasks found matching current filters.</p>
      ) : (
        <>
          <div className="task-list task-list-manager-view">
            {renderTaskContent(currentTasksPage)}
          </div>

          <div className="pagination-controls">
            <div className="page-size-selector">
              <label htmlFor="pageSizeSelect">Items per page:</label>
              <select
                id="pageSizeSelect"
                value={pageSize}
                onChange={handlePageSizeChange}
                disabled={isActionInProgress}
              >
                {PAGE_SIZE_OPTIONS.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </div>

            <div className="pagination-center-group">
              <div className="pagination-buttons">
                <button
                  onClick={goToPrevPage}
                  disabled={isPrevDisabled || isActionInProgress}
                  className="btn-pagination"
                >
                  <ChevronLeft size={16} />
                </button>
                <span className="page-info">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={goToNextPage}
                  disabled={isNextDisabled || isActionInProgress}
                  className="btn-pagination"
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>

            <div className="results-count-display">{resultsCountMessage}</div>
          </div>

          <div className="tasks-action-footer">
            <button
              className="btn-danger"
              onClick={openDeleteAllModal}
              disabled={isActionInProgress}
            >
              <Trash2 size={18} className="icon-margin-right" />
              Delete All Tasks
            </button>
          </div>
        </>
      )}

      {/* MODAL: Delete All Tasks */}
      <Modal
        isOpen={isDeleteAllModalOpen}
        onCancel={closeDeleteAllModal}
        onConfirm={handleDeleteAllTasks}
        title="Confirm Task Deletion (ALL)"
        message={deleteTaskModalContent}
        confirmButtonText={
          isActionInProgress ? "Deleting..." : "Yes, Delete All Tasks"
        }
        confirmButtonClassName={
          "btn-danger" + (isActionInProgress ? " disabled" : "")
        }
        cancelButtonClassName={
          "btn-secondary" + (isActionInProgress ? " disabled" : "")
        }
      />

      {/* MODAL: Edit Single Task */}
      <Modal
        isOpen={!!taskToEdit}
        onCancel={closeEditModal}
        onConfirm={handleSaveTask}
        title={`Edit Task: ${
          taskToEdit?.description.substring(0, 30) || "..."
        } ${taskToEdit?.description.length > 30 ? "..." : ""}`}
        message={renderTaskForm(
          editFormData,
          handleEditFormChange,
          formError,
          isActionInProgress,
        )}
        confirmButtonText={isActionInProgress ? "Saving..." : "Save Changes"}
        confirmButtonClassName={
          isSaveDisabled ? "btn-primary disabled" : "btn-primary"
        }
        cancelButtonClassName={
          "btn-secondary" + (isActionInProgress ? " disabled" : "")
        }
      />

      {/* MODAL: Delete Single Task */}
      <Modal
        isOpen={!!taskToDelete}
        onCancel={closeIndividualDeleteModal}
        onConfirm={handleConfirmIndividualDelete}
        title="Confirm Task Deletion"
        message={deleteIndividualTaskModalContent}
        confirmButtonText={
          isActionInProgress ? "Deleting..." : "Yes, Delete Task"
        }
        confirmButtonClassName={
          "btn-danger" + (isActionInProgress ? " disabled" : "")
        }
        cancelButtonClassName={
          "btn-secondary" + (isActionInProgress ? " disabled" : "")
        }
      />

      {/* NEW MODAL: Create New Task */}
      <Modal
        isOpen={isCreateModalOpen}
        onCancel={closeCreateModal}
        onConfirm={handleCreateTask}
        title="Create New Clan Task"
        message={renderTaskForm(
          createFormData,
          handleCreateFormChange,
          formError,
          isActionInProgress,
        )}
        confirmButtonText={isActionInProgress ? "Creating..." : "Create Task"}
        confirmButtonClassName={
          // Note: using createFormData here for disabled check
          isActionInProgress ||
          !createFormData.description.trim() ||
          !createFormData.difficulty
            ? "btn-success disabled"
            : "btn-success"
        }
        cancelButtonClassName={
          "btn-secondary" + (isActionInProgress ? " disabled" : "")
        }
      />
    </div>
  );
}

export default ClanTasksTab;
